/**
 * Ingestion parsing — extraído de Ingestion.jsx para que sea testeable con
 * Jest puro (sin renderizar React). Ningún cambio de comportamiento visual;
 * es la misma lógica, movida a un módulo importable.
 *
 * Responsabilidad: convertir filas de CSV crudas en Actor drafts, y detectar
 * — sin fallar en silencio — dos cosas que antes no se reportaban:
 *   1. Fechas de renovación no parseables (advertencia con fila/campo/valor).
 *   2. Duplicados evidentes dentro del mismo archivo (por correo o teléfono).
 *
 * "Cuenta importada" y "oportunidad detectada" son conceptos separados a
 * propósito: esta función solo arma actores. Generar oportunidades es
 * responsabilidad de domain/portfolio.js (ingestActors), corriendo después.
 */

/**
 * Hash determinístico (djb2) para generar un ID de actor estable a partir de
 * su identidad real (organización + correo/teléfono) — misma entrada,
 * siempre el mismo ID. Sin esto, cada reimportación del mismo archivo creaba
 * un actor NUEVO con un ID aleatorio (Math.random()) en vez de actualizar el
 * existente: el vínculo con GHL (externalRefs.ghlContactId) resuelto en una
 * importación se perdía en la siguiente porque el actor vinculado quedaba
 * huérfano y la interfaz seguía apuntando a una oportunidad vieja, sin
 * vínculo, mientras se acumulaban decenas de duplicados. No es criptográfico
 * — solo necesita ser estable, no impredecible.
 */
function stableIdFrom(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) >>> 0;
  }
  return hash.toString(36);
}

export function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/"/g, ""));
  return lines.slice(1).map((line) => {
    const cols = line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
    const row = {};
    headers.forEach((h, i) => (row[h] = cols[i] ?? ""));
    return row;
  });
}

/**
 * Convierte filas ya leídas de una hoja de Excel (array de arrays, primera
 * fila = encabezados — la forma que `XLSX.utils.sheet_to_json(sheet, {header:1})`
 * entrega) al mismo formato de objeto {encabezado_normalizado: valor} que
 * produce parseCSV — así buildActorDraftsFromRows no necesita saber si el
 * origen fue un .csv o un .xlsx. Encabezados normalizados igual que CSV
 * (minúsculas, sin espacios al borde) para que los alias de abajo
 * (ej. "Telefono_E164" → "telefono_e164") funcionen igual en ambos casos.
 * Filas totalmente vacías (fila en blanco entre datos, común en Excel) se
 * descartan, no se importan como cuentas fantasma.
 */
export function rowsFromSheetArray(sheetRows) {
  if (!sheetRows || sheetRows.length < 2) return [];
  const headers = sheetRows[0].map((h) => String(h ?? "").trim().toLowerCase());
  return sheetRows
    .slice(1)
    .filter((cols) => cols.some((c) => String(c ?? "").trim() !== ""))
    .map((cols) => {
      const row = {};
      headers.forEach((h, i) => {
        const v = cols[i];
        row[h] = v == null ? "" : String(v).trim();
      });
      return row;
    });
}

const MONTH_NAMES = {
  ene: 0, enero: 0, jan: 0, january: 0,
  feb: 1, febrero: 1, february: 1,
  mar: 2, marzo: 2, march: 2,
  abr: 3, abril: 3, apr: 3, april: 3,
  may: 4, mayo: 4,
  jun: 5, junio: 5, june: 5,
  jul: 6, julio: 6, july: 6,
  ago: 7, agosto: 7, aug: 7, august: 7,
  sep: 8, sept: 8, septiembre: 8, setiembre: 8, september: 8,
  oct: 9, octubre: 9, october: 9,
  nov: 10, noviembre: 10, november: 10,
  dic: 11, diciembre: 11, dec: 11, december: 11,
};

/**
 * Un distribuidor normalmente sabe el MES en que un cliente renueva, no el
 * día exacto — pedir una fecha completa era fricción inventada. Esta
 * función acepta mes-y-año en varias formas ("Agosto 2026", "Sep-2026",
 * "agosto de 2026", "2026-08") y resuelve internamente al día 15 de ese mes
 * (punto medio, sin sesgo hacia el inicio o el fin) para que el motor de
 * Renovaciones siga trabajando con una fecha real sin que el usuario tenga
 * que inventarla. Fechas completas ("2026-08-20", u otro formato que
 * `Date` entienda) se preservan exactas — compatibilidad con archivos
 * cargados antes de este cambio.
 * @param {string} raw
 * @returns {Date|null}
 */
export function parseRenewalInput(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return null;

  // Fecha completa ya conocida (AAAA-MM-DD, con o sin hora) — se preserva
  // exacta, no se recorta al día 15.
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const d = new Date(trimmed);
    if (!Number.isNaN(d.getTime())) return d;
  }

  // DD/MM/AAAA explícito — el formato que produce Excel al reformatear una
  // fecha ISO con configuración regional en español (hallazgo real,
  // diagnóstico WhatsApp E2E agosto 2026: "2026-08-20" se convertía en
  // "20/08/2026" solo por abrir el archivo, sin editarlo). Deliberadamente
  // NUNCA se interpreta como MM/DD/AAAA -- para Commercial Operator, en
  // este contexto, día/mes/año es la única lectura válida (nunca
  // ambigua). Validación explícita de calendario real (bisiestos, días
  // por mes) en vez de delegar en el constructor de Date, que "normaliza"
  // fechas imposibles en vez de rechazarlas (ej. new Date(2027, 1, 29) se
  // convierte silenciosamente en el 1 de marzo). Si el patrón calza pero
  // la fecha no existe, se rechaza aquí mismo -- nunca cae al fallback
  // genérico de abajo, que sí sería ambiguo para este mismo texto.
  const ddmmyyyy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (ddmmyyyy) {
    const day = Number(ddmmyyyy[1]);
    const month = Number(ddmmyyyy[2]);
    const year = Number(ddmmyyyy[3]);
    if (month >= 1 && month <= 12) {
      const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
      const daysInMonth =
        [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
      if (day >= 1 && day <= daysInMonth) {
        return new Date(Date.UTC(year, month - 1, day));
      }
    }
    return null;
  }

  // Año-mes ISO: "2026-08"
  const isoMonth = trimmed.match(/^(\d{4})-(\d{1,2})$/);
  if (isoMonth) {
    const year = Number(isoMonth[1]);
    const month = Number(isoMonth[2]) - 1;
    if (month >= 0 && month <= 11) return new Date(Date.UTC(year, month, 15));
  }

  // Nombre de mes + año, en cualquier orden y separador: "Agosto 2026",
  // "Sep-2026", "agosto de 2026", "2026 ago".
  const cleaned = _normalizeMonthText(trimmed);
  const textMatch =
    cleaned.match(/^([a-z]+)\s+(\d{4})$/) || cleaned.match(/^(\d{4})\s+([a-z]+)$/);
  if (textMatch) {
    const [, a, b] = textMatch;
    const [monthToken, year] = /^\d+$/.test(a) ? [b, a] : [a, b];
    const monthIndex = MONTH_NAMES[monthToken];
    if (monthIndex != null) return new Date(Date.UTC(Number(year), monthIndex, 15));
  }

  // Cualquier otro formato de fecha que el navegador ya entienda —
  // compatibilidad amplia con cargas anteriores.
  const generic = new Date(trimmed);
  if (!Number.isNaN(generic.getTime())) return generic;

  return null;
}

const MONTH_ONLY_ISO_RE = /^(\d{4})-(\d{1,2})$/;

function _normalizeMonthText(trimmed) {
  return trimmed
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\bde\b/g, " ")
    .replace(/[/-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * true si `raw` es una entrada de renovación con granularidad de MES (nunca
 * un día exacto) — "Agosto 2026", "Sep-2026", "2026-08". `parseRenewalInput`
 * resuelve estas al día 15 del mes como punto medio estimado (ver su propio
 * docstring); esta función existe para que el llamador (`buildActorDraftsFromRows`)
 * pueda distinguir "el día 15 estimado ya pasó, pero el mes todavía no
 * termina" (sigue siendo una renovación vigente este mes) de "el día 15
 * pasó porque el usuario dio una fecha EXACTA ya vencida" (sí debe
 * descartarse) — sin esa distinción, cualquier mes ya pasado el día 15
 * desaparecía silenciosamente del motor de Renovaciones el resto del mes.
 * @param {string} raw
 * @returns {boolean}
 */
export function isMonthGranularityRenewalInput(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return false;
  if (MONTH_ONLY_ISO_RE.test(trimmed)) return true;
  const cleaned = _normalizeMonthText(trimmed);
  return /^[a-z]+\s+\d{4}$/.test(cleaned) || /^\d{4}\s+[a-z]+$/.test(cleaned);
}

/**
 * @param {Array<Record<string,string>>} rows — filas ya parseadas del CSV
 * @param {{organizationId: string, verticalId: string}} tenant
 * @returns {{
 *   drafts: object[],
 *   dateWarnings: Array<{row:number, field:string, value:string, suggestion:string}>,
 *   duplicateWarnings: Array<{rows:number[], companyName:string, matchedBy:string}>,
 *   crossSellFormatWarnings: Array<{row:number, field:string, value:string, suggestion:string}>,
 * }}
 */
export function buildActorDraftsFromRows(rows, { organizationId, verticalId }) {
  const dateWarnings = [];
  const duplicateWarnings = [];
  const crossSellFormatWarnings = [];
  const seenKeys = new Map(); // "correo|telefono" normalizado -> primera fila (Excel, 1-based + header)
  const keyOccurrences = new Map(); // misma clave -> cuántas veces ya apareció en ESTE archivo (para el ID estable)

  const drafts = rows.map((row, idx) => {
    // Fila 1 = encabezado en el CSV/Excel; la primera fila de datos es la 2.
    const excelRow = idx + 2;

    const monthsInactiveRaw = row.months_inactive || row.meses_inactivo || "";
    const monthsInactive = monthsInactiveRaw !== "" ? Number(monthsInactiveRaw) : null;

    const renewalDateRaw = row.renewal_date || row.fecha_renovacion || "";
    let daysUntilRenewal = null;
    if (renewalDateRaw) {
      const parsed = parseRenewalInput(renewalDateRaw);
      if (!parsed) {
        dateWarnings.push({
          row: excelRow,
          field: "renewal_date",
          value: renewalDateRaw,
          suggestion:
            'Usa un mes como "Ago-2026", "Sep-2026" u "Oct-2026". La cuenta se importó, pero ' +
            "sin un mes válido no genera oportunidad de renovación hasta que lo corrijas.",
        });
      } else {
        const days = Math.round((parsed.getTime() - Date.now()) / (24 * 3600 * 1000));
        if (days >= 0) {
          daysUntilRenewal = days;
        } else if (isMonthGranularityRenewalInput(renewalDateRaw)) {
          // El día 15 es solo el punto medio estimado de un mes-y-año, no
          // una fecha que el distribuidor haya dado en serio (ver
          // isMonthGranularityRenewalInput) -- que ya haya pasado el día 15
          // no significa que la renovación de ESTE mes ya se perdió, solo
          // que no sabemos el día exacto. Se mantiene vigente mientras el
          // mes no haya terminado; se reporta como "vence ya" (0) porque no
          // hay forma de estimar mejor sin el día real.
          const endOfMonth = new Date(
            Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth() + 1, 0, 23, 59, 59)
          );
          if (endOfMonth.getTime() >= Date.now()) daysUntilRenewal = 0;
        }
      }
    } else if (String(row.proceso || "").trim().toLowerCase() === "renovaciones") {
      // Archivo de laboratorio de QA (columna "Proceso", sin fecha de
      // renovación real — es una matriz de escenarios de prueba, no una
      // cartera con fechas). "Proceso: Renovaciones" es la única señal
      // disponible de que esa fila debe entrar al motor de Renovaciones
      // para poder probar el flujo M1/detección de respuesta de extremo a
      // extremo. 14 días es una ventana cercana arbitraria pero razonable
      // para pruebas — no representa una fecha real de ningún cliente.
      daysUntilRenewal = 14;
    }

    const stage =
      daysUntilRenewal != null ? "active"
      : monthsInactive != null && monthsInactive >= 2 ? "inactive"
      : "active";

    // Venta Cruzada (Sprint 12) — la ingesta SOLO valida formato (número
    // 0-100), nunca el umbral de negocio (eso lo decide el motor). Un valor
    // fuera de rango o no numérico se reporta como advertencia visible y NO
    // se limita con clamp — la cuenta se importa igual, sin señal.
    const rawPotential = String(row.cross_sell_potential || row.potencial_venta_cruzada || "").trim();
    let crossSellPotential = null;
    if (rawPotential !== "") {
      const num = Number(rawPotential);
      if (Number.isNaN(num) || num < 0 || num > 100) {
        crossSellFormatWarnings.push({
          row: excelRow,
          field: "cross_sell_potential",
          value: rawPotential,
          suggestion: "Usa un número entre 0 y 100. La cuenta se importó, pero sin un valor válido no se evalúa para venta cruzada.",
        });
      } else {
        crossSellPotential = num;
      }
    }
    const crossSellProduct = String(row.cross_sell_product || row.producto_sugerido || "").trim() || null;
    const currentProduct = String(row.current_product || row.producto_actual || "").trim() || null;

    // Duplicados dentro del mismo archivo — por correo si existe, si no por
    // teléfono. Se advierte, nunca se combina ni se descarta sin que el
    // usuario decida.
    const email = String(row.email || "").trim().toLowerCase();
    const phone = String(row.phone || row.telefono || row.telefono_e164 || "").trim();
    const key = email || phone;
    if (key) {
      if (seenKeys.has(key)) {
        duplicateWarnings.push({
          rows: [seenKeys.get(key), excelRow],
          companyName: String(row.company_name || row.empresa || ""),
          matchedBy: email ? "correo" : "teléfono",
        });
      } else {
        seenKeys.set(key, excelRow);
      }
    }
    // Ocurrencia de esta clave dentro de ESTE archivo (0, 1, 2...) — filas
    // duplicadas en el mismo archivo deben seguir importándose como cuentas
    // separadas (regla ya probada arriba: "no se combina sin autorización"),
    // así que entran al hash para diferenciarlas entre sí. Reimportar el
    // MISMO archivo más tarde reproduce el mismo orden y el mismo conteo de
    // ocurrencias por clave, así que cada fila real vuelve a caer en el
    // mismo ID que la vez anterior.
    const keyOccurrence = key ? (keyOccurrences.get(key) || 0) : 0;
    if (key) keyOccurrences.set(key, keyOccurrence + 1);

    // Determinístico cuando hay correo/teléfono (misma identidad real entre
    // importaciones -> mismo ID -> saveActor lo actualiza en vez de
    // duplicarlo, ver comentario de stableIdFrom). Sin correo ni teléfono no
    // hay identidad estable con la que comparar entre archivos distintos,
    // así que se mantiene aleatorio (comportamiento anterior).
    return {
      id: key
        ? `a-${stableIdFrom(`${organizationId}|${key}|${keyOccurrence}`)}`
        : `a-${Math.random().toString(36).slice(2, 10)}`,
      organizationId,
      vertical: verticalId,
      stage,
      companyName: String(row.company_name || row.empresa || ""),
      contactName: String(row.contact_name || row.contacto || row.nombre || "Sin contacto"),
      email: String(row.email || ""),
      phone,
      segment: String(row.industry_segment || row.segmento || "SMB"),
      historicalRevenue: {
        amount: Number(row.total_billed_usd || row.facturacion_total || 0),
        currency: "USD",
      },
      monthlyAverage: {
        amount: Math.round(Number(row.total_billed_usd || 0) / 12) || 0,
        currency: "USD",
      },
      source: "csv",
      isDemo: false,
      // BUG real encontrado en auditoría funcional (agosto 2026): esto
      // fabricaba "6 meses inactivo" para CUALQUIER fila sin ese dato —
      // incluida una fila con cross_sell_potential/producto válidos, que
      // nunca declaró inactividad. crossSell.js bloquea Venta Cruzada
      // cuando monthsInactive >= 2, así que ese valor inventado volvía
      // Venta Cruzada inalcanzable desde una carga CSV real sin que nadie
      // lo pidiera. Ahora, igual que los demás atributos opcionales de
      // abajo, monthsInactive solo se incluye si el archivo lo trajo —
      // "desconocido" ya no se trata como "6 meses inactivo".
      verticalAttributes: {
        ...(monthsInactive != null ? { monthsInactive } : {}),
        ...(daysUntilRenewal != null ? { daysUntilRenewal } : {}),
        ...(crossSellPotential != null ? { crossSellPotential } : {}),
        ...(crossSellProduct ? { crossSellProduct } : {}),
        ...(currentProduct ? { currentProduct } : {}),
      },
      // Vínculo externo opcional (ej. contactId de GHL) — metadata de
      // integración, nunca la identidad principal del Actor.
      ...(row.ghl_contact_id
        ? { externalRefs: { ghlContactId: String(row.ghl_contact_id) } }
        : {}),
    };
  });

  return { drafts, dateWarnings, duplicateWarnings, crossSellFormatWarnings };
}
